import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { updateMe } from "../services/authService";
import { useAuth } from "../store/AuthContext";
import { hasWithdrawalPreset, userPatchFromWithdrawalPreset, withdrawalPresetFromUser } from "../utils/withdrawalPreset";

export default function ProfilePage() {
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    phone: user?.phone || "",
    profile_photo_url: user?.profile_photo_url || "",
    current_password: "",
    new_password: "",
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [presetForm, setPresetForm] = useState(() => withdrawalPresetFromUser(user));
  const [presetSaving, setPresetSaving] = useState(false);
  const activeSection = (searchParams.get("section") || "personal").toLowerCase();

  React.useEffect(() => {
    setPresetForm(withdrawalPresetFromUser(user));
  }, [
    user?.withdrawal_preset_account_name,
    user?.withdrawal_preset_account_number,
    user?.withdrawal_preset_bank_name,
    user?.withdrawal_preset_routing_number,
  ]);

  React.useEffect(() => {
    setForm((f) => ({
      ...f,
      full_name: user?.full_name || "",
      phone: user?.phone || "",
      profile_photo_url: user?.profile_photo_url || "",
    }));
  }, [user?.full_name, user?.phone, user?.profile_photo_url]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    const payload = {
      full_name: form.full_name.trim(),
      phone: form.phone.trim(),
      profile_photo_url: form.profile_photo_url.trim(),
    };
    if (form.new_password.trim()) {
      payload.current_password = form.current_password;
      payload.new_password = form.new_password;
    }
    try {
      const { data } = await updateMe(payload);
      refreshUser(data);
      setMessage("Profile updated.");
      setForm((prev) => ({ ...prev, current_password: "", new_password: "" }));
    } catch (err) {
      const d = err.response?.data;
      if (d && typeof d === "object") {
        const parts = Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`);
        setError(parts.join(" ") || "Update failed.");
      } else {
        setError(d?.detail || "Update failed.");
      }
    }
  };

  const onSaveWithdrawalPreset = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    const bank = presetForm.bank_account_name.trim();
    const acct = presetForm.bank_account_number.trim();
    const bankName = presetForm.bank_name.trim();
    if (!bank || !acct || !bankName) {
      setError("Account name, account number, and bank name are required for a withdrawal preset.");
      return;
    }
    setPresetSaving(true);
    try {
      const { data } = await updateMe(userPatchFromWithdrawalPreset(presetForm));
      refreshUser(data);
      setMessage("Withdrawal preset saved. It will prefill the wallet withdrawal form.");
    } catch (err) {
      const d = err.response?.data;
      if (d && typeof d === "object") {
        const parts = Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`);
        setError(parts.join(" ") || "Could not save withdrawal preset.");
      } else {
        setError(d?.detail || "Could not save withdrawal preset.");
      }
    } finally {
      setPresetSaving(false);
    }
  };

  return (
    <div className="page">
      <h2>Profile</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Update your name, phone, profile photo link, or password. Email cannot be changed here.
      </p>
      <div className="section-actions">
        <Link className={`nav-link ${activeSection === "personal" ? "profile-section-link-active" : ""}`} to="/profile?section=personal">
          Personal information
        </Link>
        <Link className="nav-link" to="/kyc">
          KYC
        </Link>
        <Link className={`nav-link ${activeSection === "settings" ? "profile-section-link-active" : ""}`} to="/profile?section=settings">
          Settings
        </Link>
        <Link className={`nav-link ${activeSection === "withdrawal-preset" ? "profile-section-link-active" : ""}`} to="/profile?section=withdrawal-preset">
          Withdrawal preset
        </Link>
      </div>
      {message && <p className="msg">{message}</p>}
      {error && <p className="msg msg-error">{error}</p>}
      {activeSection === "personal" && (
        <form className="card stack" style={{ padding: 14, maxWidth: 520 }} onSubmit={onSubmit}>
          <label>
            Full name
            <input
              required
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            />
          </label>
          <label>
            Phone
            <input
              required
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </label>
          <label>
            Profile photo (image URL)
            <input
              type="url"
              value={form.profile_photo_url}
              onChange={(e) => setForm({ ...form, profile_photo_url: e.target.value })}
              placeholder="https://..."
            />
          </label>
          {form.profile_photo_url && (
            <img src={form.profile_photo_url} alt="Profile" style={{ maxWidth: 120, borderRadius: 8 }} />
          )}
          <hr style={{ border: 0, borderTop: "1px solid #e5e7eb" }} />
          <p className="muted" style={{ margin: 0 }}>
            Leave password fields empty to keep your current password.
          </p>
          <label>
            Current password (if changing password)
            <input
              type="password"
              autoComplete="current-password"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            />
          </label>
          <label>
            New password
            <input
              type="password"
              autoComplete="new-password"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            />
          </label>
          <button type="submit">Save changes</button>
        </form>
      )}
      {activeSection === "settings" && (
        <div className="card stack" style={{ padding: 14, maxWidth: 560 }}>
          <h3 style={{ margin: 0 }}>Settings</h3>
          <p className="muted" style={{ margin: 0 }}>
            Notification and preference settings panel is reserved here.
          </p>
          <p className="muted" style={{ margin: 0 }}>
            If you want, I can wire concrete investor settings next (email alerts, SMS alerts, session preferences).
          </p>
        </div>
      )}
      {activeSection === "withdrawal-preset" && (
        <form className="card stack" style={{ padding: 14, maxWidth: 560 }} onSubmit={onSaveWithdrawalPreset}>
          <h3 style={{ margin: 0 }}>Withdrawal preset</h3>
          <p className="muted" style={{ margin: 0 }}>
            Default bank details for BDT withdrawals. These fields are filled automatically when you open{" "}
            <strong>Submit withdrawal</strong> on your wallet.
          </p>
          {hasWithdrawalPreset(user) ? (
            <p className="msg msg-success" style={{ margin: 0 }}>
              Preset saved. Withdrawals will use these details unless you change them in the form.
            </p>
          ) : null}
          <label>
            Account name
            <input
              required
              value={presetForm.bank_account_name}
              onChange={(e) => setPresetForm({ ...presetForm, bank_account_name: e.target.value })}
            />
          </label>
          <label>
            Account number
            <input
              required
              value={presetForm.bank_account_number}
              onChange={(e) => setPresetForm({ ...presetForm, bank_account_number: e.target.value })}
            />
          </label>
          <label>
            Bank name
            <input
              required
              value={presetForm.bank_name}
              onChange={(e) => setPresetForm({ ...presetForm, bank_name: e.target.value })}
            />
          </label>
          <label>
            Routing / branch (optional)
            <input
              value={presetForm.routing_number}
              onChange={(e) => setPresetForm({ ...presetForm, routing_number: e.target.value })}
            />
          </label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="submit" disabled={presetSaving}>
              {presetSaving ? "Saving…" : "Save withdrawal preset"}
            </button>
            <Link className="btn-secondary" style={{ display: "inline-flex", alignItems: "center" }} to="/wallet?withdraw=new">
              Open wallet withdrawal
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}
